import React, { useState } from "react";
import {
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../store/reduxHooks";
import theme from "../../../utils/theme";
import SearchBar from "../../shared/SearchBar";
import SortableTableCell from "../../shared/SortableTableCell";
import { useSortable } from "../../shared/useSortable";
import { editButtonSx, deleteButtonSx, permissionsButtonSx } from "../../shared/buttonStyles";

import { ICompanyData } from "../../../store/mosaic/companySlice";

interface CompanyTableProps {
  handleUpdateCompanyClick: (row: ICompanyData) => void;
  handleUpdatePermissionsCompanyClick: (row: ICompanyData) => void;
  handleDelete: (id: number) => void;
}

export const CompanyTable: React.FC<CompanyTableProps> = ({
  handleUpdateCompanyClick,
  handleUpdatePermissionsCompanyClick,
  handleDelete,
}) => {
  const companyData = useAppSelector((state) => state.companySlice.companyData);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const filteredData = companyData.filter((row) =>
    row.name.toLowerCase().includes(search.toLowerCase())
  );

  const { sortedData, sortConfig, requestSort } = useSortable(filteredData, "id", "desc");

  return (
    <>
    <SearchBar value={search} onChange={setSearch} placeholder="Search companies..." />
    <TableContainer sx={{ flex: 1, overflow: "auto" }}>
    <Table aria-label="company table" stickyHeader>
      <TableHead>
        <TableRow>
          <SortableTableCell label="Company name" sortKey="name" active={sortConfig?.key === "name"} direction={sortConfig?.direction ?? "asc"} onClick={requestSort} />
          <SortableTableCell label="Users" sortKey="user_count" active={sortConfig?.key === "user_count"} direction={sortConfig?.direction ?? "asc"} onClick={requestSort} align="center" />
          <TableCell align="center" sx={{ backgroundColor: "#FAFAFA", fontWeight: 600 }}>Permissions</TableCell>
          <TableCell align="center" sx={{ backgroundColor: "#FAFAFA", fontWeight: 600 }}>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedData.length === 0 && (
          <TableRow>
            <TableCell colSpan={4} align="center" sx={{ py: 4, color: "#878787" }}>
              No companies found
            </TableCell>
          </TableRow>
        )}
        {sortedData.map((row, index: number) => (
          <TableRow
            key={index}
            sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
          >
            <TableCell component="th" scope="row">{row.name}</TableCell>
            <TableCell align="center">
              <Link
                component="button"
                onClick={() => navigate(`/mosaic/users?company=${row.id}`)}
                sx={{ color: theme.palette.brand.primary, fontWeight: 500, textDecoration: "none", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
              >
                {row.user_count ?? 0}
              </Link>
            </TableCell>
            <TableCell align="center">
              <Button variant="outlined" onClick={() => handleUpdatePermissionsCompanyClick(row)} sx={permissionsButtonSx}>
                Permissions
              </Button>
            </TableCell>
            <TableCell align="center">
              <Button variant="contained" onClick={() => handleUpdateCompanyClick(row)} sx={editButtonSx}>
                Edit
              </Button>
              <Button variant="contained" onClick={() => handleDelete(row.id)} sx={deleteButtonSx}>
                Delete
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </TableContainer>
    </>
  );
};
